// core
import { useCallback, useState, useMemo } from 'react'
// components
import { Box, CARD_WIDTH, Flex } from 'components'
import { ReactComponent as LogoIcon } from 'assets/images/svg/logo-next-retreat.svg'
import Stepper from 'components/Stepper'
// utils
import {
  IDataVenueTypes,
  COLORS,
  BOXSHADOWS,
  IRoom,
  changeMealIntoString,
  getAllImages,
  local,
} from 'utils'
import { useUploadTripImage } from 'utils/hooks'
// partials
import { GeneralInfoStep } from './GeneralInfoStep'
import { ImagesStep } from './ImagesStep'
import { AdditionalInfoStep, MIN_AMENITIES } from './AdditionalInfoStep'
import { RoomsStep } from './RoomsStep'
import { MeetingRoomsStep } from './MeetingRoomsStep'
import { OwnerInfoStep } from './OwnerInfoStep'
import { WelcomeMessage } from './WelcomeMessage'
import { OpenOnDesktop } from './OpenOnDesktop'
import { OnChange } from './OnChange'
import { ThankYouMessage } from './ThankYouMessage'
import { UploadFilesMessage } from './UploadFilesMessage'
// libraries
import { useQuery, useMutation, gql } from '@apollo/client'
import { Button } from '@nextretreat/ui-components/dist/Button'
import theme from '@nextretreat/ui-components/dist/Theme'
import { rem } from 'polished'
import styled from '@emotion/styled'
import * as Yup from 'yup'
import { Formik, Form, FormikHelpers, FormikErrors } from 'formik'
import { isMobile } from 'react-device-detect'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { toast } from 'react-toastify'

const numberStringValidation = (
  number: Yup.NumberSchema<
    number | undefined | null,
    Yup.AnyObject,
    number | undefined | null,
    '' | 'd'
  >
) =>
  number.transform((value, originalValue) => {
    if (originalValue === '') {
      return null
    } else {
      return value
    }
  })

const phoneRegex =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/

const commonRoomSchema = {
  title: Yup.string().ensure().required('Title is required!'),
  description: Yup.string().ensure().required('Description is required!'),
  quantity: numberStringValidation(
    Yup.number()
      .positive('Quantity must be positive number')
      .required('Quantity is required!')
  ),
  capacity: numberStringValidation(
    Yup.number()
      .positive('Capacity must be positive number')
      .required('Capacity is required!')
  ),
  squareMeters: numberStringValidation(
    Yup.number().positive('Square meters must be positive number').nullable()
  ),
  thumbnail: Yup.mixed().required('Image is required!'),
  photos: Yup.array().of(Yup.mixed()),
}

const roomsSchema = Yup.mixed().when('generalInfo.isEntireHouse', ([value]) =>
  !value
    ? Yup.array()
        .of(
          Yup.object().shape({
            ...commonRoomSchema,
            variations: Yup.array().when('capacity', ([value]) => {
              const capacityValidation = value
                ? Yup.number()
                    .positive('Capacity must be positive number')
                    .required('Capacity is required!')
                    .max(value, `Capacity must be lower or same as ${value}!`)
                : Yup.number().positive().required('Capacity is required!')

              return Yup.array()
                .of(
                  Yup.object().shape({
                    lowRate: numberStringValidation(
                      Yup.number()
                        .min(0, 'Low rate must be positive number')
                        .required('Low rate is required!')
                    ),
                    description: Yup.string().ensure(),
                    meal: Yup.mixed(),
                    capacity: numberStringValidation(capacityValidation),
                  })
                )
                .min(1, 'You should have at least one variation!')
                .required('You should have at least one variation!')
            }),
          })
        )
        .default([
          //@ts-ignore
          {
            title: '',
            description: '',
            variations: [{ description: '' }],
          },
        ])
    : Yup.array()
        .of(
          Yup.object().shape({
            ...commonRoomSchema,
          })
        )
        .default([
          //@ts-ignore
          {
            title: '',
            description: '',
          },
        ])
)

const venueValidationSchema = Yup.object()
  .shape({
    // first step
    generalInfo: Yup.object()
      .shape({
        name: Yup.string().ensure().required('Title is required!'),
        description: Yup.string().ensure().required('Description is required!'),
        venueType: Yup.mixed().required('Venue Type is required!'),
        destination: Yup.mixed().required('Destination is required!'),
        checkIn: Yup.string()
          .default('14:00')
          .nullable()
          .required('Check-in is required!')
          .typeError('Check-in is required!'),
        checkOut: Yup.string()
          .default('10:00')
          .nullable()
          .required('Check-out is required!')
          .typeError('Check-out is required!'),
        address: Yup.string().ensure().required('Address is required!'),
        country: Yup.string().ensure().required('Provide valid address!'),
        countryCode: Yup.string().ensure().required('Provide valid address!'),
        city: Yup.string().ensure().required('Provide valid address!'),
        coordinates: Yup.object()
          .shape({
            lat: Yup.number().required("Address couldn't be found!"),
            lng: Yup.number().required("Address couldn't be found!"),
          })
          .required("Address couldn't be found"),
        isEntireHouse: Yup.boolean(),
        price: Yup.number().when('isEntireHouse', ([value]) =>
          value
            ? numberStringValidation(
                Yup.number()
                  .positive('Price must be positive number')
                  .required('Price is required!')
              )
            : numberStringValidation(
                Yup.number().positive('Price must be positive number')
              )
        ),
        ical: Yup.string().ensure().url('ICal link must be url!'),
      })
      .defined(),

    // second step
    thumbnail: Yup.mixed().required('Thumbnail Image is required!'),
    photos: Yup.array()
      .of(Yup.mixed())
      .required('Upload at least one additional image!'),

    // third step
    additionalInfo: Yup.object()
      .shape({
        amenities: Yup.object()
          .default({})
          .required(`Check at least ${MIN_AMENITIES} amenities!`),
        customAmenities: Yup.string().ensure(),
        upload: numberStringValidation(
          Yup.number()
            .positive('Upload must be positive number')
            .integer('Upload must be integer')
        ),
        download: numberStringValidation(
          Yup.number()
            .positive('Download must be positive number')
            .integer('Download must be integer')
        ),
        carbonEmissionsRoom: numberStringValidation(
          Yup.number().positive('Room carbon emissions must be positive number')
        ),
        carbonEmissionsMeeting: numberStringValidation(
          Yup.number().positive(
            'Meeting room carbon emissions must be positive number'
          )
        ),
        hasMeetingRooms: Yup.boolean().nullable(),
      })
      .defined(),

    // fourth
    rooms: roomsSchema,

    // fifth
    meetingRooms: Yup.mixed().when(
      'additionalInfo.hasMeetingRooms',
      ([value], schema) =>
        value ? roomsSchema : schema.nullable().default(null)
    ),

    // sixth
    name: Yup.string().ensure().required('Name is required!'),
    phone: Yup.string()
      .ensure()
      .matches(phoneRegex)
      .required('Phone number is reqired!'),
    email: Yup.string().ensure().email().required('Email is reqired!'),
  })
  .defined()

export type IVenueFormValues = Yup.InferType<typeof venueValidationSchema>

const RootWrapper = styled(Box)`
  background-color: ${theme.COLORS.WHITE};
  box-shadow: ${BOXSHADOWS.DARK};
  border-radius: 8px;
  border: 1px solid ${COLORS.IRRADIANT_IRIS};
  max-width: 100%;
  padding: ${rem(24)};
  overflow: auto;
  margin-bottom: 20px;
`

const WidthWrapper = styled(Box)`
  max-width: ${CARD_WIDTH};
  width: 100%;
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  width: calc(${CARD_WIDTH} * 2);
`

const StyledBackButton = styled(Button.Secondary)`
  margin-right: ${rem(10)};
`

const Logo = styled(LogoIcon)`
  margin: ${rem(30)} 0;
  flex-shrink: 0;
`

const GET_VENUE_TYPES = gql`
  query VenueTypes {
    venueTypes {
      key
      value
    }
  }
`

const LIST_VENUE = gql`
  mutation ListVenue($input: ListVenueInput!) {
    listVenue(input: $input) {
      key
      venueId
      roomIds
      meetingRoomIds
    }
  }
`

export const FormPage = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true)
  const [showThankYouMessage, setShowThankYouMessage] = useState(false)
  const [step, setStep] = useState(0)
  const [photoUpload, setPhotoUpload] = useState<{
    end: number
    current: number
    message?: string
  } | null>(null)

  const { data } = useQuery<IDataVenueTypes>(GET_VENUE_TYPES)

  const [listVenue, { loading: venueLoading }] = useMutation(LIST_VENUE)
  const { uploadTripImage, loading: photoLoading } = useUploadTripImage()

  const startListing = () => {
    setShowWelcomeMessage(false)
  }
  const nextStep = (index?: number) => {
    setStep(index || ((prev) => prev + 1))
  }
  const prevStep = (index?: number) => {
    setStep(index || ((prev) => prev - 1))
  }

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }

    const token = await executeRecaptcha('listVenue')

    // Do whatever you want with the token

    return token
  }, [executeRecaptcha])

  const handleSubmit = useCallback(
    async (
      values: IVenueFormValues,
      { setSubmitting }: FormikHelpers<IVenueFormValues>
    ) => {
      setSubmitting(true)

      const gRecaptchaToken = await handleReCaptchaVerify()

      const castedValues = venueValidationSchema.cast(values, { assert: false })
      const copiedValues = { ...castedValues }

      const destinationId = (
        copiedValues.generalInfo.destination as { value: string }
      ).value

      const customDestination = destinationId.startsWith('custom')
        ? destinationId
        : undefined

      const inputValues = {
        gRecaptchaToken,
        property: {
          ...copiedValues.generalInfo,
          ...copiedValues.additionalInfo,
          carbonEmissionsRoom:
            copiedValues.additionalInfo.carbonEmissionsRoom || undefined,
          carbonEmissionsMeeting:
            copiedValues.additionalInfo.carbonEmissionsMeeting || undefined,
          upload: copiedValues.additionalInfo.upload || undefined,
          download: copiedValues.additionalInfo.download || undefined,
          lat: copiedValues.generalInfo.coordinates.lat,
          lon: copiedValues.generalInfo.coordinates.lng,
          venueType: (copiedValues.generalInfo.venueType as { value: string })
            .value,
          destination: customDestination,
          destinationId: customDestination ? undefined : destinationId,
          amenities: Object.keys(copiedValues.additionalInfo.amenities)
            .filter(
              (value: any) =>
                (copiedValues.additionalInfo.amenities as { key: boolean }[])[
                  value
                ]
            )
            .map((value) => Number(value)),
        },
        provider: {
          name: copiedValues.name,
          phone: copiedValues.phone,
          email: copiedValues.email,
        },
        rooms: changeMealIntoString(
          copiedValues.rooms as IRoom[],
          copiedValues.generalInfo.isEntireHouse
        ),
        meetingRooms: undefined as IRoom[] | undefined,
      }

      //@ts-ignore
      delete inputValues.property.coordinates

      if (copiedValues.additionalInfo.hasMeetingRooms) {
        inputValues.meetingRooms = changeMealIntoString(
          copiedValues.meetingRooms as IRoom[],
          copiedValues.generalInfo.isEntireHouse
        ) as IRoom[]
      }

      delete inputValues.property.hasMeetingRooms

      try {
        const venueResponse = await listVenue({
          variables: { input: inputValues },
        })

        const { key, venueId, roomIds, meetingRoomIds } =
          venueResponse.data.listVenue

        const photos = getAllImages(copiedValues)

        setPhotoUpload({ current: 0, end: photos.length, message: 'Thumbnail' })

        for (const { file, isHero, roomIndex, isMeetingRoom } of photos) {
          try {
            await uploadTripImage({
              file,
              key,
              venueId,
              isHero: !!isHero,
              roomId:
                roomIndex !== undefined
                  ? isMeetingRoom
                    ? meetingRoomIds[roomIndex]
                    : roomIds[roomIndex]
                  : undefined,
            })
          } catch {
            toast.error('Failed to upload photo')
          }

          setPhotoUpload((prev) => ({
            current: (prev?.current || 0) + 1,
            end: prev?.end || photos.length,
            message: roomIndex
              ? isMeetingRoom
                ? 'Meeting Room Photos'
                : 'Room Photos'
              : 'Photos',
          }))
        }

        local.removeItem('values')
        setShowThankYouMessage(true)
      } catch {
        // do nothing
      }

      setSubmitting(false)
    },
    [listVenue, handleReCaptchaVerify]
  )

  const onNextPageDisabled = useCallback(
    (
        step: number,
        errors: FormikErrors<IVenueFormValues>,
        amenities: { [key: string]: boolean }
      ) =>
      () => {
        const stepNotValidArray = [
          //@ts-ignore
          errors.generalInfo ? Object.values(errors.generalInfo)?.[0] : null,
          errors.thumbnail || errors.photos,
          Object.keys(amenities).length < MIN_AMENITIES
            ? `Check at least ${MIN_AMENITIES} amenities`
            : Object.values(errors.additionalInfo || {})?.[0],
          //@ts-ignore
          errors.rooms?.filter(Boolean).length
            ? //@ts-ignore
              Object.values(errors.rooms?.filter(Boolean)[0])?.[0]
            : 'Add at least one room',
          //@ts-ignore
          errors.meetingRooms?.filter(Boolean).length
            ? //@ts-ignore
              Object.values(errors.meetingRooms?.filter(Boolean)[0])?.[0]
            : 'Add at least one meeting room',
        ]

        if (typeof stepNotValidArray[step] === 'string')
          toast.error(stepNotValidArray[step] as 'string')
        else if ((stepNotValidArray[step] as [])?.length) {
          const variationValue = Object.values(
            (stepNotValidArray[step] as []).filter(Boolean)
          )?.[0]
          //@ts-ignore
          toast.error(Object.values(variationValue)?.[0])
        } else if (typeof stepNotValidArray[step] === 'object') {
          const variationValue = Object.values(
            stepNotValidArray[step] as object
          )?.[0]
          toast.error(variationValue)
        }
      },
    []
  )

  const steps = [
    <GeneralInfoStep venueTypes={data?.venueTypes} />,
    <ImagesStep />,
    <AdditionalInfoStep />,
    <RoomsStep />,
    <MeetingRoomsStep />,
    <OwnerInfoStep />,
  ]

  const stepNames = [
    'General Info',
    'Images',
    'Additional Info',
    'Rooms',
    'Meeting Rooms',
    'Owner Info',
  ]

  // VARIABLES
  const initialValues = useMemo(() => {
    const values = local.getItem('values')

    const parsedValues = values ? JSON.parse(values) : {}

    return venueValidationSchema.cast(parsedValues, { assert: false })
  }, [])

  const initialTouched = useMemo(() => {
    const values = local.getItem('values')

    const parsedValues = values ? JSON.parse(values) : {}

    Object.keys(parsedValues).forEach(
      (name) => (parsedValues[name] = !!parsedValues[name])
    )
    return parsedValues
  }, [])

  return (
    <>
      <Logo />

      {!(showWelcomeMessage || showThankYouMessage || photoUpload) ? (
        <Flex maxWidth={`calc(${CARD_WIDTH} * 2)`} mt={rem(64)} width="100%">
          {stepNames.map((text, index) => (
            <Stepper.Step key={`${text}_${index}`} active={step === index}>
              <Box>{text}</Box>
            </Stepper.Step>
          ))}
        </Flex>
      ) : null}

      <RootWrapper>
        {showWelcomeMessage ? (
          <WidthWrapper>
            <WelcomeMessage onList={startListing} />
          </WidthWrapper>
        ) : isMobile ? (
          <OpenOnDesktop />
        ) : showThankYouMessage ? (
          <WidthWrapper>
            <ThankYouMessage />
          </WidthWrapper>
        ) : photoUpload ? (
          <WidthWrapper width={`${CARD_WIDTH} !important`}>
            <UploadFilesMessage {...photoUpload} />
          </WidthWrapper>
        ) : (
          <Formik
            validateOnChange
            validateOnMount
            initialTouched={initialTouched}
            initialValues={initialValues}
            validationSchema={venueValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched }) => {
              const stepNotValidArray = [
                errors.generalInfo || !touched.generalInfo,
                errors.thumbnail || errors.photos,
                Object.keys(values.additionalInfo.amenities).length <
                  MIN_AMENITIES || errors.additionalInfo,
                errors.rooms || !touched.rooms,
                errors.meetingRooms || !touched.meetingRooms,
              ]

              return (
                <StyledForm>
                  {steps[step]}

                  <OnChange />

                  <Flex justifyContent="center">
                    {step > 0 && (
                      <StyledBackButton
                        type="button"
                        key="back"
                        onClick={() =>
                          prevStep(
                            !values.additionalInfo.hasMeetingRooms && step === 5
                              ? 3
                              : undefined
                          )
                        }
                      >
                        Back
                      </StyledBackButton>
                    )}

                    {step === steps.length - 1 ? (
                      <Button.Primary
                        key="submit"
                        disabled={
                          !!Object.keys(errors).length ||
                          venueLoading ||
                          photoLoading
                        }
                        type="submit"
                      >
                        Submit
                      </Button.Primary>
                    ) : (
                      <Button.Primary
                        key="continue"
                        type="button"
                        onClick={
                          stepNotValidArray[step]
                            ? onNextPageDisabled(
                                step,
                                errors,
                                values.additionalInfo.amenities
                              )
                            : () =>
                                nextStep(
                                  !values.additionalInfo.hasMeetingRooms &&
                                    step === 3
                                    ? 5
                                    : undefined
                                )
                        }
                      >
                        Continue
                      </Button.Primary>
                    )}
                  </Flex>
                </StyledForm>
              )
            }}
          </Formik>
        )}
      </RootWrapper>
    </>
  )
}
